import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import { FeaturePageStructuredData } from "../../config/StructuredData";

const rows = [
  { feature: "Real-time status visibility", excel: "Manual update required", simplifyte: "Always current, system-updated" },
  { feature: "Client-wise work tracking", excel: "One row per client, hard to filter", simplifyte: "Filter by client, period, or status instantly" },
  { feature: "Assigned staff per work item", excel: "Column entry, no enforcement", simplifyte: "Assigned owner with system accountability" },
  { feature: "Deadline reminders", excel: "Manual calendar entry or none", simplifyte: "Automatic reminders to staff and clients" },
  { feature: "Review and approval workflow", excel: "Not supported", simplifyte: "Structured maker-checker with log" },
  { feature: "Document collection tracking", excel: "Separate sheet or WhatsApp", simplifyte: "Built-in checklist per work item" },
  { feature: "Audit trail", excel: "None unless you track manually", simplifyte: "Every action logged automatically" },
  { feature: "Closure proof", excel: "Row colour or manual note", simplifyte: "Formal closure step with attachment" },
  { feature: "FY/period tracking", excel: "Column headers, error-prone", simplifyte: "First-class field on every work item" },
  { feature: "Multi-user collaboration", excel: "Merge conflicts and version chaos", simplifyte: "Concurrent access, no conflicts" },
];

const ExcelVsSimplifyte = () => (
  <>
    <FeaturePageStructuredData
      name="Excel vs Simplifyte for CA Firms and Professional Teams"
      description="Compare Excel spreadsheets vs Simplifyte for managing client work in CA firms. See why professional service teams replace Excel with structured workflow management."
      url="https://www.simplifyte.com/compare/excel-vs-simplifyte"
    />
    <HeaderMenu />
    <main style={{ paddingTop: "80px", minHeight: "100vh" }}>
      <section style={{ background: "linear-gradient(135deg,#093A5B 0%,#0a4d6e 100%)", padding: "60px 24px", color: "#fff", textAlign: "center" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <p style={{ fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase", opacity: 0.7, marginBottom: "16px" }}>Compare</p>
          <h1 style={{ fontSize: "clamp(26px,4vw,44px)", fontWeight: 700, lineHeight: 1.2, marginBottom: "20px" }}>Excel vs Simplifyte for CA Firms</h1>
          <p style={{ fontSize: "17px", opacity: 0.85, maxWidth: "640px", margin: "0 auto 32px" }}>Excel was built for numbers, not workflow. See why CA firms and professional teams replace spreadsheets with Simplifyte for managing client work.</p>
          <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>See Simplifyte in Action</Link>
        </div>
      </section>

      <section style={{ padding: "60px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "26px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>The problem with using Excel for client work management</h2>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "16px" }}>
            Excel is a great tool for analysis. It is a poor tool for managing workflow. CA firms that use Excel to track client work face the same recurring problems: outdated status, no reminders, no accountability, version conflicts, and no audit trail. As the client list grows, the Excel sheet becomes a liability, not a management tool.
          </p>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "32px" }}>
            Simplifyte is purpose-built for professional service workflows — every feature below reflects what actually happens in a CA firm when managing recurring client work.
          </p>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#093A5B", color: "#fff" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Feature</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Excel</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Simplifyte</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#f8fafc" : "#fff", borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 500, color: "#1e293b" }}>{row.feature}</td>
                    <td style={{ padding: "12px 16px", color: "#ef4444" }}>✗ {row.excel}</td>
                    <td style={{ padding: "12px 16px", color: "#16a34a" }}>✓ {row.simplifyte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={{ padding: "40px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: "820px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>When does Excel break down for CA firms?</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "16px" }}>
            {[
              { title: "As team size grows", desc: "Multiple people editing the same sheet creates version conflicts and overwritten data. Excel has no ownership model." },
              { title: "When clients exceed 30–40", desc: "Filtering and cross-referencing across hundreds of rows is error-prone. A missed row means a missed filing." },
              { title: "During high-volume periods", desc: "March, July, October — every deadline-heavy period shows up Excel's lack of reminders and accountability." },
              { title: "For partner oversight", desc: "Partners cannot see real-time status from an Excel file — they ask the team, who updates the sheet, who may not have." },
            ].map((item, i) => (
              <div key={i} style={{ background: "#fff", padding: "18px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{item.title}</h3>
                <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "60px 24px", textAlign: "center", background: "#093A5B", color: "#fff" }}>
        <h2 style={{ fontSize: "26px", fontWeight: 700, marginBottom: "14px" }}>Replace your Excel tracker with Simplifyte</h2>
        <p style={{ opacity: 0.8, marginBottom: "24px", fontSize: "16px" }}>Book a 20-minute demo and see how Simplifyte replaces your spreadsheet workflow.</p>
        <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "13px 30px", borderRadius: "8px", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>Book a Demo <ArrowRight size={16} /></Link>
      </section>
    </main>
    <CustomFooter />
  </>
);

export default ExcelVsSimplifyte;
